import { forwardRef, MouseEventHandler } from "react";
import Button, { ButtonProps } from "@ebsco-ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import Tooltip, { TooltipProps } from "@ebsco-ui/tooltip";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import cn from "classnames";

import "./tool-button.scss";

export interface ToolButtonProps extends Omit<ButtonProps, "styleType"> {
  onClick: MouseEventHandler<HTMLButtonElement>;
  isSelected?: boolean;
  defaultIcon: IconProp;
  selectedIcon?: IconProp;
  className?: string;
  title: string;
  selectedTitle?: string;
  tooltipProps?: Partial<TooltipProps>;
}

const ToolButton = forwardRef<HTMLButtonElement, ToolButtonProps>(
  (
    {
      title,
      selectedTitle,
      onClick,
      isSelected = false,
      defaultIcon,
      selectedIcon,
      className,
      tooltipProps = {},
      ...rest
    },
    ref,
  ) => {
    const getBlocksWith = createBemBlockBuilder(["eb-tool-button", className]);
    const toolIcon = ((isSelected && selectedIcon) as IconProp) || defaultIcon;
    const toolTitle = (isSelected && selectedTitle) || title;

    return (
      <div className={getBlocksWith()}>
        <Tooltip
          className={getBlocksWith("__tooltip")}
          title={toolTitle}
          {...tooltipProps}
        >
          <Button
            className={cn(
              getBlocksWith("__button"),
              isSelected && getBlocksWith("__button--selected"),
            )}
            styleType="flat-icon"
            onClick={onClick}
            data-auto="tool-button"
            aria-label={toolTitle}
            ref={ref}
            {...rest}
          >
            <FontAwesomeIcon
              className={getBlocksWith("__icon")}
              icon={toolIcon}
              data-auto="font-awesome-icon"
            />
          </Button>
        </Tooltip>
      </div>
    );
  },
);

ToolButton.displayName = "ToolButton";

export default ToolButton;
